import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SharedNav from './SharedNav';
import './OnboardingTable.css';

const PLAIN_FIELDS = [
  { key: 'reportTime', label: 'Reporting Time' },
  { key: 'dispatchTime', label: 'Dispatch Time' },
  { key: 'onRoadHours', label: 'On Road Hours' },
];

const CLAMP_FIELDS = [
  { key: 'trainingPay', label: 'Training Pay' },
  { key: 'regularPay', label: 'Regular Pay' },
  { key: 'topDriverPay', label: 'Top Driver Pay' },
  { key: 'trainingDuration', label: 'No. of Training Days' },
  { key: 'overtimeAvailable', label: 'Overtime?' },
  { key: 'overtimeRate', label: 'Overtime Rate?' },
];

const BONUS_FIELDS = [
  { key: 'stopBonus', label: 'Stop Bonus' },
  { key: 'performanceBonus', label: 'Performance' },
  { key: 'safetyBonus', label: 'Safety' },
  { key: 'attendanceBonus', label: 'Attendance' },
];

const BENEFIT_FIELDS = [
  { key: 'ipads', label: 'iPads' },
  { key: 'medical', label: 'Medical' },
  { key: 'dental', label: 'Dental' },
  { key: 'vision', label: 'Vision' },
  { key: 'retirement401k', label: '401K' },
  { key: 'retirement401kMatching', label: '401K Matching' },
];

const EDITABLE_FIELDS = [
  ...PLAIN_FIELDS.map(f => f.key),
  ...CLAMP_FIELDS.map(f => f.key),
  ...BONUS_FIELDS.map(f => f.key),
  ...BENEFIT_FIELDS.map(f => f.key),
  'stopBonusDetails',
  'otherBenefits',
];

function rowKey(row) {
  return `${row.onboarding_id}_${row.station_index ?? 'x'}`;
}

function Clamp({ value }) {
  const v = (value || '').trim();
  if (!v) return <span className="ot-blank">—</span>;
  const long = v.length > 90;
  return (
    <div className={`ot-clamp${long ? ' ot-clamp--overflow' : ''}`} title={long ? v : undefined}>
      <span className="ot-clamp-text">{v}</span>
    </div>
  );
}

function Checkbox({ checked, label, editable, onToggle }) {
  return (
    <span
      className={`ot-chk-item${checked ? ' ot-chk-on' : ''}${editable ? ' ot-chk-editable' : ''}`}
      onClick={editable ? onToggle : undefined}
    >
      <span className="ot-chk-box">{checked ? '✓' : ''}</span>
      <span className="ot-chk-label">{label}</span>
    </span>
  );
}

export default function OnboardingTable({ user, onLogout }) {
  const canEdit = ['admin', 'recruiting_admin'].includes(user?.role?.toLowerCase());

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/onboarding-table');
      setRows(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load onboarding submissions');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditingKey(rowKey(row));
    const d = {};
    EDITABLE_FIELDS.forEach(f => { d[f] = row[f]; });
    setDraft(d);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const setField = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async (row) => {
    setSaving(true);
    try {
      const fields = {};
      EDITABLE_FIELDS.forEach(f => { fields[f] = draft[f]; });
      await axios.patch(`/api/onboarding/${row.onboarding_id}/station`, {
        stationIndex: row.station_index,
        fields,
      });
      setRows(prev => prev.map(r => (rowKey(r) === rowKey(row) ? { ...r, ...fields } : r)));
      setEditingKey(null);
      setDraft(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save changes');
    }
    setSaving(false);
  };

  return (
    <div className="ot-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="ot-body">
        <div className="ot-header">
          <div className="ot-subtitle">
            {rows.length} station-level submissions across P&amp;D onboarding forms. Bonuses and Benefits show every checkbox exactly as offered on the form.
            {canEdit && ' Click Edit to update any row.'}
          </div>
        </div>

        {loading ? (
          <div className="ot-loading">Loading…</div>
        ) : error ? (
          <div className="ot-error">{error}</div>
        ) : (
          <div className="ot-table-wrap mtn-scroll-x">
            <table className="ot-table">
              <thead>
                <tr>
                  <th className="ot-col-customer">Customer</th>
                  {PLAIN_FIELDS.map(f => <th key={f.key} className="ot-col-narrow">{f.label}</th>)}
                  {CLAMP_FIELDS.map(f => (
                    <th key={f.key} className={f.key === 'trainingPay' || f.key === 'regularPay' || f.key === 'topDriverPay' ? 'ot-col-pay' : 'ot-col-narrow'}>
                      {f.label}
                    </th>
                  ))}
                  <th className="ot-col-bonuses">Bonuses?</th>
                  <th className="ot-col-benefits">Benefits?</th>
                  <th className="ot-col-pay">Benefits Start</th>
                  {canEdit && <th className="ot-col-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const key = rowKey(row);
                  const isEditing = editingKey === key;
                  return (
                    <tr key={key}>
                      <td className="ot-col-customer">
                        <span className={`ot-customer-name ${row.account_status === 'Live' ? 'ot-customer-live' : 'ot-customer-not-live'}`} title={row.account_status || 'Not a Live account'}>
                          {row.company_name}
                        </span>
                        {row.multi_station && <span className="ot-station-tag">Station {row.station_number || '—'}</span>}
                      </td>

                      {PLAIN_FIELDS.map(f => (
                        <td key={f.key}>
                          {isEditing ? (
                            <input
                              className="ot-input"
                              value={draft[f.key] || ''}
                              onChange={e => setField(f.key, e.target.value)}
                            />
                          ) : (
                            row[f.key] || <span className="ot-blank">—</span>
                          )}
                        </td>
                      ))}

                      {CLAMP_FIELDS.map(f => (
                        <td key={f.key} className={f.key === 'trainingPay' || f.key === 'regularPay' || f.key === 'topDriverPay' ? 'ot-col-pay' : 'ot-col-narrow'}>
                          {isEditing ? (
                            <textarea
                              className="ot-textarea"
                              rows={3}
                              value={draft[f.key] || ''}
                              onChange={e => setField(f.key, e.target.value)}
                            />
                          ) : (
                            <Clamp value={row[f.key]} />
                          )}
                        </td>
                      ))}

                      <td className="ot-col-bonuses">
                        <div className="ot-chk-col">
                          {BONUS_FIELDS.map(f => (
                            <Checkbox
                              key={f.key}
                              checked={isEditing ? draft[f.key] : row[f.key]}
                              label={f.label}
                              editable={isEditing}
                              onToggle={() => setField(f.key, !draft[f.key])}
                            />
                          ))}
                        </div>
                        {(isEditing ? draft.stopBonus : row.stopBonus) && (
                          isEditing ? (
                            <textarea
                              className="ot-textarea ot-textarea--detail"
                              rows={2}
                              placeholder="Stop bonus details"
                              value={draft.stopBonusDetails || ''}
                              onChange={e => setField('stopBonusDetails', e.target.value)}
                            />
                          ) : (
                            row.stopBonusDetails && <div className="ot-detail-note">{row.stopBonusDetails}</div>
                          )
                        )}
                      </td>

                      <td className="ot-col-benefits">
                        <div className="ot-chk-grid-2col">
                          {BENEFIT_FIELDS.map(f => (
                            <Checkbox
                              key={f.key}
                              checked={isEditing ? draft[f.key] : row[f.key]}
                              label={f.label}
                              editable={isEditing}
                              onToggle={() => setField(f.key, !draft[f.key])}
                            />
                          ))}
                        </div>
                      </td>

                      <td className="ot-col-pay">
                        {isEditing ? (
                          <textarea
                            className="ot-textarea"
                            rows={3}
                            value={draft.otherBenefits || ''}
                            onChange={e => setField('otherBenefits', e.target.value)}
                          />
                        ) : (
                          <Clamp value={row.otherBenefits} />
                        )}
                      </td>

                      {canEdit && (
                        <td className="ot-col-actions">
                          {isEditing ? (
                            <div className="ot-action-row">
                              <button className={`mtn-btn mtn-btn-send${saving ? ' mtn-btn-disabled' : ''}`} disabled={saving} onClick={() => saveEdit(row)}>
                                {saving ? 'Saving…' : 'Save'}
                              </button>
                              <button className={`mtn-btn mtn-btn-neutral${saving ? ' mtn-btn-disabled' : ''}`} disabled={saving} onClick={cancelEdit}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button className="mtn-btn mtn-btn-edit" onClick={() => startEdit(row)}>
                              <span className="mtn-ic">✏</span>Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
